import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Phone, Mail, Download } from "lucide-react";
import { COMPANY, FULL_SCOPE } from "@/data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = { name: "", phone: "", area: "", service: FULL_SCOPE[0], notes: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received! Our senior master polisher will call you within 60 minutes.");
      setForm(INITIAL);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please call us directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full text-xs px-3.5 py-3 border border-slate-300 rounded focus:border-[#1e3a8a] focus:outline-none bg-white text-slate-800";
  const labelCls = "block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1";

  return (
    <section id="contact" className="bg-[#f1f5f9] text-[#0f172a] py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#1e3a8a] block mb-2">Direct Contractor Access</span>
              <h2 className="display-font text-4xl uppercase tracking-tight text-[#0f172a]">
                Request Site Visit &amp; Quotation
              </h2>
            </div>

            <div className="bg-white p-6 border border-slate-200 rounded-lg shadow-sm space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">Direct Calling &amp; WhatsApp</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <a data-testid="contact-phone-1-link" href={`tel:${COMPANY.phone1Raw}`} className="font-bold text-slate-900 mono-font text-base hover:text-[#1e3a8a] transition-colors inline-flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#1e3a8a]" aria-hidden="true" /> {COMPANY.phone1}
                  </a>
                  <span className="text-[10px] font-bold uppercase bg-blue-100 text-[#1e3a8a] px-2 py-0.5 rounded">Primary</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <a data-testid="contact-phone-2-link" href={`tel:${COMPANY.phone2Raw}`} className="font-bold text-slate-900 mono-font text-base hover:text-[#1e3a8a] transition-colors inline-flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" aria-hidden="true" /> {COMPANY.phone2}
                  </a>
                  <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-600 px-2 py-0.5 rounded">Alternate</span>
                </div>
              </div>
              <a
                data-testid="contact-whatsapp-btn"
                href={COMPANY.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider rounded transition-colors"
              >
                Chat on WhatsApp
              </a>
              <div className="pt-2">
                <span className="text-xs text-slate-500 block">Official Email:</span>
                <a data-testid="contact-email-link" href={`mailto:${COMPANY.email}`} className="text-xs font-bold text-slate-900 mono-font hover:text-[#1e3a8a] transition-colors inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" aria-hidden="true" /> {COMPANY.email}
                </a>
              </div>
            </div>

            <div className="bg-white p-6 border border-slate-200 rounded-lg shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Registered Contractor Office</h3>
              <p data-testid="contact-address" className="text-xs text-slate-700 leading-relaxed font-medium">{COMPANY.address}.</p>
            </div>

            <div className="bg-white p-6 border border-slate-200 rounded-lg shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Our Visiting Card</h3>
              <figure className="rounded overflow-hidden border border-slate-200 mb-4">
                <img
                  src="/company-card.jpeg"
                  alt="Anas Enterprises Civil Interior Contractor visiting card — marble and granite specialists, Malad East Mumbai"
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </figure>
              <a
                data-testid="download-card-btn"
                href="/company-card.jpeg"
                download="Anas-Enterprises-Visiting-Card.jpeg"
                className="w-full inline-flex items-center justify-center gap-2 py-3 bg-slate-900 hover:bg-slate-700 text-white text-xs font-bold uppercase tracking-wider rounded transition-colors"
              >
                <Download className="w-4 h-4" aria-hidden="true" /> Download Visiting Card
              </a>
            </div>

            <div id="gst-info" data-testid="gst-info-card" className="bg-[#1e3a8a] text-white p-6 rounded-lg">
              <span className="text-[10px] font-bold uppercase tracking-widest text-blue-200 block mb-1">Government Tax Registration</span>
              <h3 className="font-bold text-lg mb-2">GST Compliant Contractor</h3>
              <div className="bg-[#172554] p-3 rounded mono-font text-xs tracking-wider text-blue-100">
                GSTIN: {COMPANY.gst}
              </div>
              <p className="text-[11px] text-blue-200 mt-2">Formal tax invoices provided for housing societies, corporate offices, and commercial interior sites.</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white p-8 sm:p-10 border border-slate-300 rounded-xl shadow-lg">
              <h3 className="display-font text-2xl uppercase tracking-tight text-[#0f172a] mb-2">Schedule Free Site Measurement</h3>
              <p className="text-xs text-slate-500 mb-6">Enter your floor requirement; our senior master polisher will contact you within 60 minutes.</p>

              <form data-testid="enquiry-form" onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="enquiry-name" className={labelCls}>Full Name</label>
                    <input id="enquiry-name" data-testid="enquiry-name-input" type="text" required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="enquiry-phone" className={labelCls}>Phone Number</label>
                    <input id="enquiry-phone" data-testid="enquiry-phone-input" type="tel" required placeholder="Mobile / WhatsApp number" value={form.phone} onChange={set("phone")} className={inputCls} />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="enquiry-area" className={labelCls}>Mumbai Area / Pin Code</label>
                    <input id="enquiry-area" data-testid="enquiry-area-input" type="text" required placeholder="e.g. Malad, Bandra, Powai" value={form.area} onChange={set("area")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="enquiry-service" className={labelCls}>Required Service</label>
                    <select id="enquiry-service" data-testid="enquiry-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                      {FULL_SCOPE.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="enquiry-notes" className={labelCls}>Approximate Square Feet / Notes</label>
                  <textarea id="enquiry-notes" data-testid="enquiry-notes-input" rows={3} placeholder="Approx area in sq ft, current floor condition or hairline cracks..." value={form.notes} onChange={set("notes")} className={inputCls} />
                </div>

                <div className="pt-2">
                  <button
                    data-testid="enquiry-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 bg-[#1e3a8a] hover:bg-[#1e40af] text-white text-xs font-bold uppercase tracking-wider rounded transition-colors shadow disabled:opacity-60 inline-flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                    {submitting ? "Submitting..." : "Submit for Instant Quotation"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
